/**
 * React Query-backed auth hook.
 *
 * The "user" is derived from the JWT access token stored in localStorage. We
 * keep it as a React Query atom (`['auth', 'session']`) so any component can
 * subscribe to changes via `useQueryClient().invalidateQueries(...)`.
 *
 * Login / logout are mutations. Login persists the token pair on success and
 * triggers a session re-read; logout calls the API to revoke the refresh
 * token, then clears storage and re-reads the (now empty) session.
 *
 * `isLoading` reflects the initial session bootstrap (one tick on mount).
 * The mutations expose their own loading state via `loginMutation.isPending`.
 */

import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import {
  clearTokens,
  decodeJwt,
  getAccessToken,
  getRefreshToken,
  isAccessTokenExpired,
  setTokens,
  type JwtClaims,
} from '@/lib/auth';

interface AuthTokensResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

export interface AuthUser {
  uid: number;
  tid: number;
  /** platform | reseller | customer. Empty string for tokens minted before the
   *  claim was added — treat as a non-reseller customer. */
  tenantType: '' | 'platform' | 'reseller' | 'customer';
  role: string;
  email: string;
  name: string;
  initial: string;
  /** True when the current session was opened via "view as customer". */
  impersonating: boolean;
  /** Reseller admin who launched the impersonation, when impersonating=true. */
  actingEmail?: string;
}

const SESSION_QUERY_KEY = ['auth', 'session'] as const;

function deriveName(email: string): string {
  // Pretty-print "camila.romero@…" → "Camila Romero" until the API returns a
  // proper display name. Best-effort, never throws.
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || email;
}

function claimsToUser(claims: JwtClaims | null): AuthUser | null {
  if (!claims) return null;
  const name = deriveName(claims.email);
  const initial = (name.charAt(0) || claims.email.charAt(0) || '?').toUpperCase();
  return {
    uid: claims.uid,
    tid: claims.tid,
    tenantType: claims.ttp ?? '',
    role: claims.role,
    email: claims.email,
    name,
    initial,
    impersonating: claims.imp === true,
    actingEmail: claims.act_email,
  };
}

async function fetchSession(): Promise<AuthUser | null> {
  const accessToken = getAccessToken();
  if (accessToken && !isAccessTokenExpired(accessToken)) {
    return claimsToUser(decodeJwt(accessToken));
  }
  // If the access token is missing or expired but we still hold a refresh
  // token, hit /v1/auth/refresh through the api client. The client persists
  // the new tokens on success and throws UnauthorizedError on failure.
  if (!getRefreshToken()) {
    return null;
  }
  try {
    const response = await api.post<AuthTokensResponse>('/v1/auth/refresh', {
      refresh_token: getRefreshToken(),
    });
    setTokens(response.access_token, response.refresh_token);
    return claimsToUser(decodeJwt(response.access_token));
  } catch {
    clearTokens();
    return null;
  }
}

export interface LoginInput {
  email: string;
  password: string;
}

export function useAuth(): {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingIn: boolean;
  isLoggingOut: boolean;
  error: Error | null;
  login: (input: LoginInput) => Promise<AuthUser | null>;
  logout: () => Promise<void>;
} {
  const queryClient = useQueryClient();

  // First-paint bootstrap: react-query marks the query as "loading" only the
  // first time it runs. After that, `isLoading` is false and we rely on the
  // mutation flags to show in-flight state.
  const session = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: async (input: LoginInput): Promise<AuthUser | null> => {
      const response = await api.post<AuthTokensResponse>('/v1/auth/login', {
        email: input.email,
        password: input.password,
      });
      setTokens(response.access_token, response.refresh_token);
      return claimsToUser(decodeJwt(response.access_token));
    },
    onSuccess: (user) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        try {
          await api.post<void>('/v1/auth/logout', { refresh_token: refreshToken });
        } catch {
          // Non-fatal: even if the API call fails (network, expired token),
          // we still want to drop the local session.
        }
      }
      clearTokens();
    },
    onSettled: () => {
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      // Invalidate every other query so caches tied to the previous tenant
      // don't leak across sessions.
      void queryClient.invalidateQueries();
    },
  });

  const login = useCallback(
    (input: LoginInput) => loginMutation.mutateAsync(input),
    [loginMutation],
  );

  const logout = useCallback(() => logoutMutation.mutateAsync(), [logoutMutation]);

  const user = session.data ?? null;

  return useMemo(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading: session.isLoading,
      isLoggingIn: loginMutation.isPending,
      isLoggingOut: logoutMutation.isPending,
      error: (loginMutation.error as Error | null) ?? null,
      login,
      logout,
    }),
    [
      user,
      session.isLoading,
      loginMutation.isPending,
      loginMutation.error,
      logoutMutation.isPending,
      login,
      logout,
    ],
  );
}
